import {
  AuthenticationStrategyInvalidEmailError,
  UnexpectedAuthenticationStrategyError,
} from 'modules/api-client';
import { Form, FormErrorMessage } from 'modules/common-ui';
import { FormField, TextInput } from 'modules/design-system';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type SignInFormValues = {
  email: string;
};

type Props = {
  onSubmit: (email: string) => Promise<void>;
};

export const SignInForm = ({ onSubmit }: Props) => {
  const { t } = useTranslation('user', { keyPrefix: 'user.signIn' });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignInFormValues>();

  const handleFormSubmit = async ({ email }: SignInFormValues) => {
    try {
      await onSubmit(email);
    } catch (err) {
      if (!(err instanceof Error)) {
        setError('root', { message: t('errors.unexpected') });
        return;
      }
      const message =
        err instanceof AuthenticationStrategyInvalidEmailError
          ? t('errors.invalidEmail')
          : err instanceof UnexpectedAuthenticationStrategyError
            ? t('errors.unexpected')
            : err.message;
      setError('root', { message });
    }
  };

  return (
    <Form
      onSubmit={handleSubmit(handleFormSubmit)}
      actions={{
        submit: {
          label: t('form.submit.label'),
          variant: 'primary',
          disabled: isSubmitting,
          buttonProps: { block: true },
        },
      }}
    >
      <FormField
        label={t('form.email.label')}
        htmlFor="signin-email"
        errorMessage={errors.email?.message}
        required
      >
        <TextInput
          id="signin-email"
          type="email"
          autoComplete="username"
          autoFocus
          fullWidth
          valid={!errors.email}
          {...register('email', {
            required: t('form.email.required'),
          })}
        />
      </FormField>

      {errors.root && (
        <FormErrorMessage>{errors.root.message}</FormErrorMessage>
      )}
    </Form>
  );
};
